import { Dispatch, SetStateAction } from 'react'
import { Observable } from 'rxjs'
import { APIProjectRequest, APIProjectResponse } from '../models'
import store from '../store'
import { useService, useServiceBatch } from '../utils/hook'
import { buildQueryFromObject } from '../utils/parser/query'
import request, { AbortableRequest, RequestResponse } from '../utils/request'
import { ActionType as ErrorActions } from '../store/error/actions'

export function useProjects(
  query: Record<string, string | number | undefined>
): [
  RequestResponse<APIProjectResponse[]> | undefined,
  Dispatch<SetStateAction<Record<string, string | number | undefined>>>,
  Observable<RequestResponse<APIProjectResponse[]>> | undefined
] {
  return useService<
    APIProjectResponse[],
    Record<string, string | number | undefined>
  >(
    (query) =>
      request.getRequest(buildQueryFromObject(query, 'api/project/?'))<
        APIProjectResponse[]
      >(),
    query,
    (error) => {
      if (error instanceof DOMException) return
      store.dispatch({
        type: ErrorActions.SET_BY_STATUS,
        payload: { statusCode: error.status, message: error.message }
      })
    }
  )
}
export function useProject(
  id: number | string
): [
  RequestResponse<APIProjectResponse> | undefined,
  Dispatch<SetStateAction<{ id: string | number | undefined }>>,
  Observable<RequestResponse<APIProjectResponse>> | undefined
] {
  return useService<APIProjectResponse, { id: number | string | undefined }>(
    (query) =>
      request.getRequest(`api/project/${query.id || 0}`)<APIProjectResponse>(),
    { id },
    (error) => {
      if (error instanceof DOMException) return
      store.dispatch({
        type: ErrorActions.SET_BY_STATUS,
        payload: { statusCode: error.status, message: error.message }
      })
    }
  )
}

export function postProject(
  data: APIProjectRequest
): AbortableRequest<APIProjectResponse> {
  return request.postRequest<APIProjectRequest, APIProjectResponse>(
    'api/project/',
    data
  )()
}

export function patchProject(
  data: APIProjectRequest
): AbortableRequest<APIProjectResponse> {
  return request.patchRequest<APIProjectRequest, APIProjectResponse>(
    'api/project/',
    data
  )()
}

export function deleteProject(
  id: number | string
): AbortableRequest<APIProjectResponse> {
  return request.deleteRequest<APIProjectResponse>(`api/project/${id}`)()
}

export function useProjectsInBatch<T extends { page: number }>(
  initialQuery: T
): [
  APIProjectResponse[] | undefined,
  Dispatch<SetStateAction<T>>,
  Observable<APIProjectResponse[]> | undefined
] {
  return useServiceBatch('api/project/?', initialQuery)
}
